import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectBMCWithRating,
  setIsBMCWithRatingDetailsPopupOpen,
  setBMCWithRatingPopupData,
  getBMCWithRating,
  setNumberOfRowsPerPage,
  setBMCWithRatingGridCurrentPage,
  setSendRequestToBMCPopupData,
} from "src/redux/slices/bmcWithRatingSlice";
import BMCWithRatingDetailsPopup from "./BMCWithRatingDetailsPopup";
import { useTranslation } from "react-i18next";
import { getAssignedBuildingsLookup } from "src/redux/slices/lookupSlice";
import { isArabic } from "src/utils/helpers";
import GridPaginator from "src/components/GridPaginator/GridPaginator";
import { getCompanyPaymentStatus } from "src/utils/common";
import moment from "moment";
import { BMCRatings, BMCsWithRatingStatuses } from "src/resources/enum";

import { ReactComponent as ExcellentIcon } from "src/assets/icons/excellent.svg";
import { ReactComponent as GoodIcon } from "src/assets/icons/good.svg";
import { ReactComponent as FairIcon } from "src/assets/icons/average.svg";
import { ReactComponent as WeakIcon } from "src/assets/icons/weak.svg";

const BMCWithRatingGrid = () => {
  const dispatch = useDispatch();

  const bmcWithRatingData = useSelector(selectBMCWithRating);

  const gridCurrentPage = useSelector(
    (state: any) => state.bmcWithRating.gridCurrentPage
  );
  const { totalRowsCount, numberOfRowsPerPage } = useSelector(
    (state: any) => state.bmcWithRating
  );
  const { t } = useTranslation([
    "common",
    "assignBuilding",
    "registration",
    "civilDefense",
    "invoice",
  ]);
  const tableRef = useRef<DataTable<(typeof bmcWithRatingData)[0]>>(null);

  useEffect(() => {
    dispatch(getBMCWithRating() as any);
    dispatch(getAssignedBuildingsLookup() as any);
  }, []);

  const companyNameTemplate = (rowData: any) => {
    return (
      <div onClick={() => handleBuildingNumberClick(rowData)}>
        {rowData.name}
      </div>
    );
  };

  const ratingTemplate = (rowData: any) => {
    const ratingText = rowData?.ratingText;
    let icon;
    if (ratingText === BMCRatings.Excellent) {
      icon = <ExcellentIcon />;
    } else if (ratingText === BMCRatings.Good) {
      icon = <GoodIcon />;
    } else if (ratingText === BMCRatings.Fair) {
      icon = <FairIcon />;
    } else {
      icon = <WeakIcon />;
    }

    return (
      <div className="rating-col d-flex gap-2 align-items-center font-weight-700">
        <span>{icon}</span> <span>{rowData?.rating}%</span>
        <span>({t(ratingText?.toLowerCase())})</span>
      </div>
    );
  };

  const handleBuildingNumberClick = (rowData: any) => {
    dispatch(
      setBMCWithRatingPopupData({
        field: "name",
        value: rowData.name,
      })
    );

    dispatch(setBMCWithRatingPopupData({ field: "id", value: rowData.id }));
    dispatch(setSendRequestToBMCPopupData({ bmcId: rowData.id }));
    dispatch(setIsBMCWithRatingDetailsPopupOpen(true));
  };

  const PaymentStatusTemplate = (rowData: any) => {
    const paymentStatus = getCompanyPaymentStatus(rowData?.paymentStatus);
    return (
      <span
        className={`oval ${
          rowData?.paymentStatus === BMCsWithRatingStatuses.Cleared
            ? "green-oval"
            : "red-oval"
        }`}
      >
        {t(paymentStatus)}
      </span>
    );
  };
  const rowClassName = (rowData: any) => {
    if (moment(rowData.civilDefenseLicenseExpiry).isBefore(moment())) {
      return "expired";
    }
    return "";
  };
  return (
    <div className="grid-outer-wrapper">
      <div className="grid-inner-wrapper">
        <DataTable
          ref={tableRef}
          lazy
          className="maintenance-requests-grid"
          value={bmcWithRatingData}
          emptyMessage={t("emptyRecords")}
          scrollable
          rowClassName={rowClassName}
        >
          <Column
            field="Name"
            header={t("companyName")}
            body={companyNameTemplate}
            className="case-id-col lg-col"
            frozen
            alignFrozen={isArabic() ? "right" : "left"}
          ></Column>
          <Column
            field="rating"
            header={t("rating")}
            body={ratingTemplate}
            className="md-col"
          ></Column>
          <Column
            field="paymentStatus"
            header={t("paymentStatus")}
            className="lg-col"
            body={PaymentStatusTemplate}
          ></Column>
          <Column
            field="numberOfBuildings"
            header={t("numberOfBuildings")}
            className="md-col"
          ></Column>
          <Column
            field="contactPersonName"
            header={t("contactPersonName")}
            className="lg-col"
          ></Column>
          <Column
            field="phoneNumber"
            header={t("phoneNumber")}
            className="md-col"
          ></Column>
          <Column
            field="tradeLicenseNumber"
            header={t("tradeLicenseNumber")}
            className="lg-col"
          ></Column>
        </DataTable>
      </div>
      <GridPaginator
        totalRowsCount={totalRowsCount}
        currentPage={gridCurrentPage}
        numberOfRowsPerPage={numberOfRowsPerPage}
        setNumberOfRowsPerPage={setNumberOfRowsPerPage}
        setGridCurrentPage={setBMCWithRatingGridCurrentPage}
        tableRef={tableRef}
      />
      <BMCWithRatingDetailsPopup />
    </div>
  );
};

export default BMCWithRatingGrid;
